import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, User, Globe, Search, Loader, Download, Shield, Users, TrendingUp, Calendar, ChevronLeft, ChevronRight, Monitor, FileText } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { generatePdfReport } from '../../utils/pdfGenerator';

const ROLE_LABELS = {
  global_admin: { label: 'Global Admin', color: 'bg-red-100 text-red-700' },
  company_admin: { label: 'Administrador', color: 'bg-purple-100 text-purple-700' },
  agent: { label: 'Agente', color: 'bg-blue-100 text-blue-700' },
  viewer: { label: 'Visualizador', color: 'bg-gray-100 text-gray-600' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const parseUA = (ua) => {
  if (!ua) return '—';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Mobile')) return 'Móvil';
  return 'Otro';
};

const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => (
  <motion.div
    className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-md border border-white/20 flex items-center gap-4"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
      color === 'blue' ? 'bg-blue-100' : color === 'purple' ? 'bg-purple-100' : color === 'green' ? 'bg-green-100' : 'bg-indigo-100'
    }`}>
      <Icon className={`w-6 h-6 ${
        color === 'blue' ? 'text-blue-600' : color === 'purple' ? 'text-purple-600' : color === 'green' ? 'text-green-600' : 'text-indigo-600'
      }`} />
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
    </div>
  </motion.div>
);

const AccessHistoryPage = () => {
  const { isAdmin, isGlobalAdmin } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  // Debounce: espera 400ms tras el último keystroke antes de buscar
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getAccessLogs({ page, limit: 20, days, search });
      if (res.success) {
        setLogs(res.logs || []);
        setPagination(res.pagination);
      }
    } catch (e) {
      console.error('Error cargando accesos:', e);
    } finally {
      setLoading(false);
    }
  }, [page, days, search]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await apiService.getAccessLogStats(days);
      if (res.success) setStats(res.stats);
    } catch (e) {
      console.error('Error cargando stats:', e);
    } finally {
      setStatsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (!isAdmin && !isGlobalAdmin) { navigate('/admin'); return; }
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats, isAdmin, isGlobalAdmin, navigate]);

  useEffect(() => { setPage(1); }, [days, search]);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const res = await apiService.getAccessLogsAll({ days, search });
      const allLogs = res.logs || [];
      const header = ['Usuario', 'Email', 'Rol', 'Agencia', 'IP', 'Navegador', 'Fecha y Hora'].join(',');
      const rows = allLogs.map(l => [
        l.user?.name || '—', l.user?.email || '—',
        ROLE_LABELS[l.user?.role]?.label || l.user?.role || '—',
        l.user?.agency || '—', l.ip_address || '—',
        parseUA(l.user_agent), formatDate(l.login_date)
      ].map(v => `"${v}"`).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `accesos_${days}dias.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exportando CSV:', e);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const res = await apiService.getAccessLogsAll({ days, search });
      const allLogs = res.logs || [];
      const headers = ['Usuario', 'Email', 'Rol', 'Agencia', 'IP', 'Navegador', 'Fecha y Hora'];
      const rows = allLogs.map(l => [
        l.user?.name || '—',
        l.user?.email || '—',
        ROLE_LABELS[l.user?.role]?.label || l.user?.role || '—',
        l.user?.agency || '—',
        l.ip_address || '—',
        parseUA(l.user_agent),
        formatDate(l.login_date)
      ]);
      generatePdfReport(`Historial de Accesos (últimos ${days} días)`, headers, rows, `accesos_${days}dias`);
    } catch (e) {
      console.error('Error exportando PDF:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <motion.div className="mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-4xl font-bold text-gray-900 mb-2">Historial de Accesos</h2>
          <p className="text-gray-500">Monitoreo de inicios de sesión con IP, dispositivo y fecha de cada acceso.</p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={TrendingUp} label="Accesos en el período" value={statsLoading ? '...' : stats?.totalLogins} color="blue" />
          <StatCard icon={Users} label="Usuarios únicos" value={statsLoading ? '...' : stats?.uniqueUsers} color="purple" />
          <StatCard icon={Clock} label="Accesos hoy" value={statsLoading ? '...' : stats?.loginsToday} color="green" />
          <StatCard icon={Shield} label="Días analizados" value={days} color="indigo" />
        </div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 mb-6 shadow-md border border-white/20 flex flex-wrap gap-3 items-center justify-between"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        >
          <div className="flex flex-wrap gap-3 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Buscar por nombre, email o IP..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:outline-none w-64"
              />
            </div>
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:outline-none"
            >
              <option value={1}>Hoy (1 día)</option>
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={60}>Últimos 60 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={365}>Último año</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              PDF
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              CSV
            </button>
          </div>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-md border border-white/20 overflow-hidden"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs">
                      <th className="py-3 px-5 text-left">Usuario</th>
                      <th className="py-3 px-5 text-left">Rol</th>
                      <th className="py-3 px-5 text-left">Agencia</th>
                      <th className="py-3 px-5 text-left">IP</th>
                      <th className="py-3 px-5 text-left">Navegador</th>
                      <th className="py-3 px-5 text-left">Fecha y Hora</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <AnimatePresence>
                      {logs.length > 0 ? logs.map((log, i) => {
                        const role = ROLE_LABELS[log.user?.role] || { label: log.user?.role || '—', color: 'bg-gray-100 text-gray-600' };
                        return (
                          <motion.tr
                            key={log.id}
                            className="hover:bg-blue-50/40 transition-colors"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.02 }}
                          >
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-2">
                                {log.user?.avatar ? (
                                  <img src={log.user.avatar} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200" onError={e => e.target.style.display = 'none'} />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                    <User className="w-4 h-4 text-blue-500" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{log.user?.name || '—'}</p>
                                  <p className="text-xs text-gray-400">{log.user?.email || '—'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${role.color}`}>{role.label}</span>
                            </td>
                            <td className="py-3 px-5 text-gray-600">{log.user?.agency || '—'}</td>
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-1 text-gray-700 font-mono text-xs">
                                <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                {log.ip_address || '—'}
                              </div>
                            </td>
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-1 text-gray-500 text-xs">
                                <Monitor className="w-3.5 h-3.5 flex-shrink-0" />
                                {parseUA(log.user_agent)}
                              </div>
                            </td>
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-1 text-gray-600 text-xs">
                                <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                {formatDate(log.login_date)}
                              </div>
                            </td>
                          </motion.tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={6} className="py-16 text-center text-gray-400">
                            No hay registros de acceso para el período seleccionado.
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 text-sm text-gray-500">
                  <span>
                    Mostrando {((page - 1) * 20) + 1}–{Math.min(page * 20, pagination.total)} de {pagination.total} registros
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="font-medium text-gray-700">Pág. {page} / {pagination.totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={!pagination.hasMore}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AccessHistoryPage;